import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStandard } from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { useModalOpened } from 'record-it-components/hooks/useModalOpened/useModalOpened';
import { useSpecificParams } from 'record-it-components/hooks/useSpecificParams/useSpecificParams';
import { IconDeprecated, IconType } from 'record-it-components/misc/IconDeprecated/IconDeprecated';
import { CreateEntityButtonConfig, HeaderConfig, ManageEntitiesContentLayout } from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { WorkTimeEntry } from '../../../classes/entities/WorkTimeEntry/workTimeEntryTypes';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { ExportAdminWorkTimeDialog } from '../../workTime/ExportAdminWorkTimeDialog/ExportAdminWorkTimeDialog';
import { WorkTimeEntryList } from '../../workTime/WorkTimeEntryList/WorkTimeEntryList';
import { useWorkTimeAdminPageFilters } from './useWorkTimeAdminPageFilters';
export type WorkTimeAdminPageProps = PropsWithoutChildren<Record<never, never>>;
export const WorkTimeAdminPage: FunctionComponent<WorkTimeAdminPageProps> = React.memo(props => {
  const $ = _c(29);
  prepareProps({
    props,
    ignoreChildren: true
  });
  const {
    getRequiredCurrentUserPermissions
  } = useCurrentUser();
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      requiredParamNames: ["userGroupId", "userId"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    userGroupId,
    userId
  } = useSpecificParams(t0);
  let t1;
  if ($[1] !== userGroupId || $[2] !== userId) {
    t1 = builder => builder.addHomePage().addWorkTimeAdminPage(userGroupId, userId);
    $[1] = userGroupId;
    $[2] = userId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useConfigureBreadcrumbsWithBuilder(t1);
  const workTimeEntries = useEntities(EntityName.WorkTimeEntry, "getAll");
  let t2;
  if ($[4] !== workTimeEntries) {
    t2 = workTimeEntries.sort(_temp);
    $[4] = workTimeEntries;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const sortedWorkTimeEntries = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      closeOnBackDropClick: true
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    close: closeExportModal,
    open: openExportModal,
    modalProps: exportModalProps
  } = useModalOpened(t3);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      visible: false
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const createEntityButtonConfig = t4;
  let t5;
  if ($[8] !== t) {
    t5 = t("components.page.WorkTimeAdminPage.pageTitle");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== openExportModal) {
    t6 = () => [<ButtonStandard key="WorkTimeAdminPage.ExportWorkTimeButton" onClick={openExportModal} data-testid="WorkTimeAdminPage.ExportWorkTimeButton"><IconDeprecated type={IconType.Regular} name="file-export" /></ButtonStandard>];
    $[10] = openExportModal;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = {
      iconConfig: iconConfigurations.prefixed.page.WorkTimeAdminPage.titleIcon,
      title: t5,
      renderAdditionalRightContent: t6
    };
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const headerConfig = t7;
  let t8;
  if ($[15] !== getRequiredCurrentUserPermissions || $[16] !== userGroupId || $[17] !== userId) {
    t8 = {
      userGroupId,
      userId,
      getRequiredCurrentUserPermissions
    };
    $[15] = getRequiredCurrentUserPermissions;
    $[16] = userGroupId;
    $[17] = userId;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const filters = useWorkTimeAdminPageFilters(t8);
  const renderEntities = _temp2;
  let t9;
  if ($[19] !== filters || $[20] !== headerConfig || $[21] !== sortedWorkTimeEntries) {
    t9 = <ManageEntitiesContentLayout headerConfig={headerConfig} createEntityButtonConfig={createEntityButtonConfig} entities={sortedWorkTimeEntries} filters={filters} renderEntities={renderEntities} />;
    $[19] = filters;
    $[20] = headerConfig;
    $[21] = sortedWorkTimeEntries;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== closeExportModal || $[24] !== exportModalProps) {
    t10 = <ExportAdminWorkTimeDialog {...exportModalProps} onCancelClick={closeExportModal} data-testid="WorkTimeAdminPage.ExportWorkTimeDialog" />;
    $[23] = closeExportModal;
    $[24] = exportModalProps;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <>{t9}{t10}</>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
});
function _temp(a, b) {
  return b.endDate.localeCompare(a.endDate);
}
function _temp2(currentPageWorkTimeEntries) {
  return <WorkTimeEntryList workTimeEntries={currentPageWorkTimeEntries} />;
}